/**
 * Data Freshness & Source Confidence Components
 * 
 * Provides transparency about data sources and freshness across the UI.
 * Shows where data comes from and how recent it is.
 */
import React, { useState, useEffect } from 'react';
import { 
  Database, 
  Cloud, 
  FileSpreadsheet, 
  FileText, 
  Edit3,
  Clock,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Info,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Source icons mapping
const SOURCE_ICONS = {
  manual: Edit3,
  pdf_quote: FileText,
  bank_csv: FileSpreadsheet,
  xero: Cloud
};

// Source colors
const SOURCE_COLORS = {
  manual: 'var(--accent-primary)',
  pdf_quote: 'var(--status-info)',
  bank_csv: 'var(--status-success)',
  xero: '#1E40AF'  // Xero blue
};

/**
 * Small inline badge showing the data source
 */
export function DataSourceBadge({ source, showLabel = true, size = 'sm' }) {
  const Icon = SOURCE_ICONS[source] || Database;
  const color = SOURCE_COLORS[source] || 'var(--text-muted)';
  
  const sourceLabels = {
    manual: 'Manual',
    pdf_quote: 'PDF Quote',
    bank_csv: 'Bank CSV',
    xero: 'Xero'
  };
  
  const sizeStyles = {
    xs: { padding: '2px 6px', fontSize: '0.625rem', iconSize: 10 },
    sm: { padding: '3px 8px', fontSize: '0.6875rem', iconSize: 12 },
    md: { padding: '4px 10px', fontSize: '0.75rem', iconSize: 14 }
  };
  
  const style = sizeStyles[size] || sizeStyles.sm;
  
  return (
    <span 
      data-testid={`source-badge-${source}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        background: `${color}15`,
        color: color,
        padding: style.padding,
        borderRadius: 'var(--radius-full)',
        fontSize: style.fontSize,
        fontWeight: '500',
        whiteSpace: 'nowrap'
      }}
    >
      <Icon size={style.iconSize} />
      {showLabel && (sourceLabels[source] || source)}
    </span>
  );
}

/**
 * Freshness timestamp display
 */
export function FreshnessTimestamp({ 
  lastUpdated, 
  lastUpdatedRelative, 
  isStale = false,
  stalenessMessage = null,
  source = null,
  size = 'sm',
  showIcon = true
}) {
  const sizeStyles = {
    xs: { fontSize: '0.625rem', iconSize: 10 },
    sm: { fontSize: '0.6875rem', iconSize: 12 },
    md: { fontSize: '0.75rem', iconSize: 14 }
  };
  
  const style = sizeStyles[size] || sizeStyles.sm;
  
  return (
    <span 
      data-testid="freshness-timestamp"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: style.fontSize,
        color: isStale ? 'var(--status-warning)' : 'var(--text-muted)'
      }}
      title={lastUpdated ? new Date(lastUpdated).toLocaleString() : undefined}
    >
      {showIcon && (
        isStale ? <AlertTriangle size={style.iconSize} /> : <Clock size={style.iconSize} />
      )}
      {source && <span style={{ fontWeight: '500' }}>{source}:</span>}
      {lastUpdatedRelative || 'Never'}
      {isStale && stalenessMessage && (
        <span style={{ marginLeft: '4px', fontStyle: 'italic' }}>
          ({stalenessMessage})
        </span>
      )}
    </span>
  );
}

/**
 * Data staleness warning banner (non-intrusive)
 */
export function StalenessWarning({ sources, minimal = false }) {
  const staleSources = sources?.filter(s => s.is_stale) || [];
  
  if (staleSources.length === 0) return null;
  
  if (minimal) {
    return (
      <span 
        data-testid="staleness-warning-minimal"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          fontSize: '0.6875rem',
          color: 'var(--status-warning)'
        }}
      >
        <AlertTriangle size={12} />
        Some data may be out of date
      </span>
    );
  }
  
  return (
    <div 
      data-testid="staleness-warning"
      style={{
        background: 'rgba(245, 158, 11, 0.08)',
        border: '1px solid rgba(245, 158, 11, 0.2)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-sm) var(--space-md)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-sm)',
        marginBottom: 'var(--space-md)'
      }}
    >
      <AlertTriangle size={16} style={{ color: 'var(--status-warning)', flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <span style={{ fontSize: '0.8125rem', color: 'var(--status-warning)' }}>
          Data may be out of date:
        </span>
        <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginLeft: '4px' }}>
          {staleSources.map(s => s.display_name).join(', ')}
        </span>
      </div>
    </div>
  );
}

/**
 * AI Response transparency footer
 */
export function AIDataSourcesFooter({ dataSources }) {
  if (!dataSources) return null;
  
  const { data_sources, stale_warnings, context_string, has_stale_data } = dataSources;
  
  return (
    <div 
      data-testid="ai-data-sources-footer"
      style={{
        marginTop: 'var(--space-md)',
        paddingTop: 'var(--space-md)',
        borderTop: '1px solid var(--glass-border)'
      }}
    >
      <div style={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        gap: 'var(--space-sm)',
        fontSize: '0.75rem',
        color: 'var(--text-muted)'
      }}>
        <Info size={14} style={{ flexShrink: 0, marginTop: '1px' }} />
        <div>
          <div style={{ marginBottom: '4px' }}>
            <strong>Insight based on:</strong>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {data_sources?.map((source, idx) => (
              <span key={idx} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <DataSourceBadge source={source.source?.toLowerCase().replace(' ', '_').replace('(read-only)', '').trim()} showLabel={false} size="xs" />
                {source.source} ({source.last_sync})
                {idx < data_sources.length - 1 && <span style={{ color: 'var(--text-dimmed)' }}>,</span>}
              </span>
            ))}
          </div>
          {has_stale_data && stale_warnings?.length > 0 && (
            <div style={{ marginTop: '6px', color: 'var(--status-warning)', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <AlertTriangle size={12} />
              {stale_warnings.join('; ')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Complete data freshness panel for Finance pages
 */
export function DataFreshnessPanel({ minimal = false, showRefresh = false }) {
  const { orgId, getAuthHeaders } = useAuth();
  const [freshness, setFreshness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFreshness = async () => {
    if (!orgId) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/freshness/${orgId}`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setFreshness(data);
      }
    } catch (err) {
      console.error('Error fetching freshness:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFreshness();
  }, [orgId]);

  if (loading || error || !freshness) return null;

  if (minimal) {
    return (
      <div 
        data-testid="data-freshness-minimal"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-md)',
          padding: 'var(--space-sm) var(--space-md)',
          background: 'var(--glass-bg)',
          borderRadius: 'var(--radius-md)',
          fontSize: '0.75rem'
        }}
      >
        <Database size={14} style={{ color: 'var(--text-muted)' }} />
        <span style={{ color: 'var(--text-muted)' }}>Data Sources:</span>
        {freshness.sources.filter(s => s.record_count > 0 || s.last_updated).map((source, idx) => (
          <span key={source.source_type} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <DataSourceBadge source={source.source_type} size="xs" />
            <span style={{ color: source.is_stale ? 'var(--status-warning)' : 'var(--text-muted)' }}>
              {source.last_updated_relative}
            </span>
          </span>
        ))}
        {freshness.stale_sources.length > 0 && (
          <StalenessWarning sources={freshness.sources} minimal />
        )}
      </div>
    );
  }

  return (
    <div 
      data-testid="data-freshness-panel"
      style={{
        background: 'var(--glass-bg)',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-md)',
        marginBottom: 'var(--space-lg)'
      }}
    >
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        marginBottom: 'var(--space-md)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
          <Database size={16} style={{ color: 'var(--accent-primary)' }} />
          <span style={{ fontWeight: '600', fontSize: '0.875rem', color: 'var(--text-primary)' }}>
            Data Sources
          </span>
          {freshness.overall_status === 'current' && (
            <span style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '4px',
              fontSize: '0.6875rem',
              color: 'var(--status-success)'
            }}>
              <CheckCircle size={12} />
              All current
            </span>
          )}
        </div>
        {showRefresh && (
          <button 
            onClick={fetchFreshness}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text-muted)',
              padding: '4px',
              borderRadius: 'var(--radius-sm)'
            }}
          >
            <RefreshCw size={14} />
          </button>
        )}
      </div>

      <StalenessWarning sources={freshness.sources} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--space-md)' }}>
        {freshness.sources.map((source) => {
          const Icon = SOURCE_ICONS[source.source_type] || Database;
          const color = SOURCE_COLORS[source.source_type] || 'var(--text-muted)';
          
          return (
            <div 
              key={source.source_type}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--space-sm)',
                padding: 'var(--space-sm)',
                background: source.is_stale ? 'rgba(245, 158, 11, 0.05)' : 'transparent',
                borderRadius: 'var(--radius-sm)'
              }}
            >
              <div style={{
                width: '28px',
                height: '28px',
                borderRadius: 'var(--radius-sm)',
                background: `${color}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Icon size={14} style={{ color }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ 
                  fontSize: '0.8125rem', 
                  fontWeight: '500', 
                  color: 'var(--text-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  {source.display_name}
                  {source.is_read_only && (
                    <span style={{ fontSize: '0.625rem', color: 'var(--text-muted)' }}>(read-only)</span>
                  )}
                </div>
                <div style={{ 
                  fontSize: '0.6875rem', 
                  color: source.is_stale ? 'var(--status-warning)' : 'var(--text-muted)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  {source.is_stale && <AlertTriangle size={10} />}
                  {source.last_updated_relative}
                </div>
                {source.record_count > 0 && (
                  <div style={{ fontSize: '0.625rem', color: 'var(--text-dimmed)' }}>
                    {source.record_count} records
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Entity source label (for individual items like invoices, quotes)
 */
export function EntitySourceLabel({ 
  source = 'manual', 
  createdAt, 
  sourceReference,
  showTimestamp = true 
}) {
  return (
    <div 
      data-testid="entity-source-label"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-sm)',
        fontSize: '0.75rem',
        color: 'var(--text-muted)'
      }}
    >
      <DataSourceBadge source={source} size="xs" />
      {sourceReference && (
        <span style={{ color: 'var(--text-secondary)' }}>
          {sourceReference}
        </span>
      )}
      {showTimestamp && createdAt && (
        <>
          <ChevronRight size={10} />
          <span>{new Date(createdAt).toLocaleDateString('en-GB')}</span>
        </>
      )}
    </div>
  );
}

/**
 * Hook to fetch data freshness
 */
export function useDataFreshness() {
  const { orgId, getAuthHeaders } = useAuth();
  const [freshness, setFreshness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!orgId) return;

    fetch(`${API_URL}/api/freshness/${orgId}`, {
      headers: getAuthHeaders()
    })
      .then(res => res.json())
      .then(data => {
        setFreshness(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [orgId, getAuthHeaders]);

  return { freshness, loading, error };
}

export default {
  DataSourceBadge,
  FreshnessTimestamp,
  StalenessWarning,
  AIDataSourcesFooter,
  DataFreshnessPanel,
  EntitySourceLabel,
  useDataFreshness
};
